export interface CoachingCultureColors {
  black: string;
  body: string;
  pink: string;
  bg: string;
  bgLight: string;
  primaryLight: string;
  star: string;
  secondary: string;
  primary: string;
  orange: string;
  blue: string;
  green: string;
  grey: string;
  grey0: string;
  grey1: string;
  grey2: string;
  grey3: string;
  grey4: string;
  grey5: string;
  grey6: string;
  grey7: string;
  danger: string;
  positive: string;
  warning: string;
  hub: string;
  [key: string]: string;
}

export type CoachingCultureColorName = Extract<
  keyof CoachingCultureColors,
  string
>;

/** Named steps of the type scale, usable as e.g. fontSize="small". */
export type FontSizeName =
  | "caption"
  | "small"
  | "body"
  | "title"
  | "section"
  | "page"
  | "display";

export type FontSizeScale = number[] & Record<FontSizeName, number>;

export type RadiusScale = number[] &
  Record<"sm" | "md" | "lg" | "full", number>;

export type ShadowScale = string[] & Record<"pop" | "modal", string>;

export interface CoachingCultureTheme {
  baseline: number;
  baseLineHeight: number;
  baseFontSize: number;
  borderRadius: RadiusScale;
  button: {
    borderRadius: number;
    defaultColor: string;
    filledTextColor: string;
    defaultFilled?: boolean;
    /**
     * Unfilled buttons are neutral (white, grey border, dark text) rather
     * than outlined in their colour. Defaults to false for older themes.
     */
    neutralSecondary?: boolean;
  };
  panel: {
    borderRadius: number;
    defaultColor: string;
    backgroundColor: keyof CoachingCultureColors;
    /** Draw a 1px border round panels. Defaults to false. */
    bordered?: boolean;
    /** Index into `shadows` for panels. Defaults to 1. */
    shadow?: number;
  };
  breakpoints: string[];
  shadows: ShadowScale;
  borders: string[];
  fonts: Record<string, string>;
  fontSizes: FontSizeScale;
  colors: CoachingCultureColors;
  getSafeColor(input: string): string;
  space: number[];
}

const baseline = 6;
const baseFontSize = 14;

/**
 * Fixed pixel sizes. The old scale was in em, so sizes multiplied when
 * components were nested (0.8 x 0.8 = 0.64). Indexes keep roughly their old
 * meaning at the top level, with nothing below 12px:
 *   0-2 -> 12, 3 -> 14 (body), 4 -> 18, 5 -> 22, 6 -> 28, then display sizes.
 */
const fontSizes: FontSizeScale = Object.assign(
  [12, 12, 12, 14, 18, 22, 28, 34, 42, 52, 64],
  {
    caption: 12,
    small: 13,
    body: 14,
    title: 16,
    section: 18,
    page: 22,
    display: 28,
  },
);

const borderRadius: RadiusScale = Object.assign([0, 6, 8, 12], {
  sm: 6,
  md: 8,
  lg: 12,
  full: 999,
});

const shadows: ShadowScale = Object.assign(
  [
    "none",
    "0 2px 5px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.2)",
    "0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)",
    "0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23)",
  ],
  {
    /** Menus, popovers, toasts. */
    pop: "0 4px 16px rgba(38, 36, 31, 0.10)",
    /** Modals only. */
    modal: "0 12px 40px rgba(38, 36, 31, 0.18)",
  },
);

// Warm neutrals: every text grey (body, black, grey0-2) passes WCAG AA on
// white and on the page background. "grey" is for icons and dividers (3:1),
// grey3-7 are for borders and fills.
const defaultColors: CoachingCultureColors = {
  black: "#26241F",
  body: "#3D3A35",
  bg: "#F7F6F3",
  bgLight: "#466375",
  primaryLight: "#FABA4C",
  star: "#F7CA00",
  secondary: "#64B0B2",
  primary: "#E69833",
  pink: "#DF837A",
  orange: "#E69833",
  blue: "#48B2D2",
  green: "#65AC67",
  grey: "#8A867F",
  grey0: "#1C1B18",
  grey1: "#63605A",
  grey2: "#716D66",
  grey3: "#DDD9D2",
  grey4: "#E8E6E1",
  grey5: "#EFEDE8",
  grey6: "#F4F3EF",
  grey7: "#FBFAF8",
  danger: "#c0392b",
  positive: "#71BC44",
  warning: "#E69833",
  hub: "#0C7695",

  // Surfaces, borders and text by role.
  surface: "#FFFFFF",
  page: "#F7F6F3",
  sunken: "#EFEDE8",
  border: "#E8E6E1",
  borderStrong: "#D6D3CC",
  textMuted: "#63605A",
  text: "#26241F",

  // Status: text on its own soft tint (all pass AA).
  dangerText: "#A3281C",
  dangerSoft: "#FBE9E7",
  warningText: "#8A5300",
  warningSoft: "#FDF1DC",
  positiveText: "#2F6B30",
  positiveSoft: "#E8F3E6",
  infoText: "#1F5F75",
  infoSoft: "#E3F1F6",

  // Content categories. Distinct from the accent so they never blend in.
  conversationsText: "#7A4508",
  conversationsSoft: "#FBEEDD",
  feedbackText: "#4B3F8F",
  feedbackSoft: "#ECE9F7",
  goalsText: "#3C6B2D",
  goalsSoft: "#EAF1E4",
  learningText: "#74345F",
  learningSoft: "#F3E6F0",
};

export const getDefaultThemeColors = () => ({ ...defaultColors });

const defaultTheme: CoachingCultureTheme = {
  baseline,
  baseLineHeight: 1.5, //calcLineHeight(baseFontSize),
  baseFontSize,
  borderRadius,
  button: {
    borderRadius: 8,
    defaultColor: "body",
    filledTextColor: "white",
    neutralSecondary: true,
  },
  // Flat cards: a thin border, 12px corners, no shadow and no coloured top
  // bar. Pass a colour to a Panel to opt back in to the bar.
  panel: {
    borderRadius: 3,
    defaultColor: "none",
    backgroundColor: "white",
    bordered: true,
    shadow: 0,
  },
  breakpoints: ["768px", "1024px", "1400px", "1920px"],
  shadows,
  fonts: {
    heading: "Montserrat,Helvetica,sans-serif",
    body: "Montserrat,Helvetica,sans-serif",
  },
  fontSizes,
  colors: getDefaultThemeColors(),
  borders: ["none", `1px solid ${getDefaultThemeColors().grey3}`],
  space: [0, 6, 12, 18, 24, 36, 48, 72, 96],
  getSafeColor(input) {
    if (this.colors[input]) {
      return this.colors[input];
    }

    return input;
  },
};

export const getDefaultTheme = () => ({ ...defaultTheme });

/**
 * A type-scale size for styled-components CSS:
 *   font-size: ${typeSize("small")};
 */
export const typeSize =
  (name: FontSizeName) =>
  (props: { theme: { fontSizes?: Partial<FontSizeScale> } }) =>
    `${props.theme.fontSizes?.[name] ?? fontSizes[name]}px`;
